/**
 * ChargingHistory
 * --
 */
import { useState, useEffect } from 'react';
import {
  Button,
  Table,
  Tag,
  Descriptions,
  Divider,
  Flex,
  Radio,
  Input,
  InputNumber,
} from 'antd';
import { Content, Title, ModalLayout } from 'components';
import moment from 'moment';
import { stringToMoneyFormat } from 'utils';
import Formatter from 'utils/Formatter';
// import * as XLSX from 'xlsx/xlsx.mjs';
import { JM_PAY_TYPE } from 'constants/JMPayConstant';
import {
  CheckCircleOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { downloadXlsx } from 'utils/XlsxDownloader';
import { ORDER_ROUTE } from 'constants/OrderConstants';
import { SIGNUP_PATH, USER_SETTING } from 'constants/UserContants';
import { Link } from 'react-router-dom';
import { encrypt } from 'utils/Crypto';

/**----------------------------------------
 * [Component] JM페이 > 유저목록
 * ----------------------------------------
 */
const ChargingHistory = ({
  data,
  currentRow,
  onSelect,
  currentTab,
  onIssue,
  cashBillInfo,
  setCashBillInfo,
  initialValue,
}) => {
  /* ===== STATE ===== */
  const [expansionUser, setExpansionUser] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [orderData, setOrderData] = useState(null);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  const getData = (data, type) => {
    switch (type) {
      case 'order': {
        const newOrderData = data.product_information.map((item) => {
          const couponInfo = data.coupon_info
            ? data.coupon_info.reduce((acc, cur) => {
                const { coupon_name, discount_menu } = cur;
                const mapDiscountMenu = new Map(
                  discount_menu.map((dis) => [dis.menu_id, dis])
                );

                const discountMenu = mapDiscountMenu.get(item.menu_id);

                if (discountMenu) {
                  acc.push({
                    coupon_name,
                    discount_price: discountMenu.discount_price,
                  });
                }

                return acc;
              }, [])
            : [];

          return {
            ...item,
            coupon_info: couponInfo,
            order_id: data.order_id,
          };
        });
        return newOrderData;
      }
      case 'shop_order': {
        const newShopOrderData = data.parcel_service.map((item) => {
          const { product, coupon_info, parcel_service_id } = item;

          return {
            parcel_service_id,
            ...product,
            coupon_name: coupon_info?.policy_coupon?.coupon_name ?? '-',
            shop_order_id: data.shop_order_id,
          };
        });
        return newShopOrderData;
      }
      default:
        return [];
    }
  };

  const getColumns = (type) => {
    switch (type) {
      case 'order': {
        return [
          {
            key: 'order_id',
            title: '주문번호',
            dataIndex: 'order_id',
          },
          {
            key: 'menu_name',
            title: '메뉴명',
            dataIndex: 'menu_name',
          },
          {
            key: 'menu_option',
            title: '옵션',
            dataIndex: 'menu_option',
            render: (option) =>
              !option ? (
                '-'
              ) : (
                <Flex vertical align="flex-start" style={{ fontSize: 13 }}>
                  {option?.map((item) => (
                    <Flex>
                      {item?.option_title}
                      <Flex vertical>
                        {item?.option?.map((option) => {
                          return (
                            <Flex gap={5}>
                              <p>({option?.option_name || '-'}) |</p>
                              <p>
                                <>
                                  {stringToMoneyFormat(
                                    option?.option_price || 0,
                                    '원'
                                  )}{' '}
                                  *{' '}
                                  {stringToMoneyFormat(
                                    option?.count || 1,
                                    '개'
                                  )}
                                </>{' '}
                                =
                              </p>
                              <p>
                                {stringToMoneyFormat(
                                  item?.option?.reduce(
                                    (acc, cur) =>
                                      acc + cur.option_price * (cur.count || 1),
                                    0
                                  ),
                                  '원'
                                )}
                              </p>
                            </Flex>
                          );
                        })}
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              ),
          },
          {
            key: 'price',
            title: '가격',
            dataIndex: 'price',
            render: (item) => stringToMoneyFormat(item),
          },
          {
            key: 'num',
            title: '수량',
            dataIndex: 'num',
          },
          {
            key: 'coupon_info',
            title: '쿠폰 할인',
            dataIndex: 'coupon_info',
            width: 300,
            render: (item, menu) =>
              !item.length ? (
                '-'
              ) : (
                <Flex vertical gap={4}>
                  {item.map((coupon) => (
                    <Flex
                      key={`coupon-${coupon.coupon_name}-${menu.menu_id}`}
                      justify="space-between"
                      align="center"
                    >
                      <p>{coupon.coupon_name}</p>
                      <p>{stringToMoneyFormat(coupon.discount_price)}</p>
                    </Flex>
                  ))}
                </Flex>
              ),
          },
        ];
      }
      case 'shop_order': {
        return [
          {
            key: 'shop_order_id',
            title: '주문번호',
            dataIndex: 'shop_order_id',
          },
          {
            key: 'parcel_service_id',
            title: '상품별 주문번호',
            dataIndex: 'parcel_service_id',
          },
          {
            key: 'product_name',
            title: '상품명',
            dataIndex: 'product_name',
          },
          {
            key: 'product_option',
            title: '옵션',
            dataIndex: 'product_option',
            render: (option) =>
              !option ? (
                '-'
              ) : (
                <Flex vertical align="flex-start" style={{ fontSize: 13 }}>
                  {option?.map((item) => (
                    <Flex>
                      {item?.option_title}
                      <Flex vertical>
                        {item?.option?.map((option) => {
                          return (
                            <Flex gap={5}>
                              <p>({option?.option_name || '-'}) |</p>
                              <p>
                                <>
                                  {stringToMoneyFormat(
                                    option?.option_price || 0,
                                    '원'
                                  )}{' '}
                                  *{' '}
                                  {stringToMoneyFormat(
                                    option?.count || 1,
                                    '개'
                                  )}
                                </>{' '}
                                =
                              </p>
                              <p>
                                {stringToMoneyFormat(
                                  item?.option?.reduce(
                                    (acc, cur) =>
                                      acc + cur.option_price * (cur.count || 1),
                                    0
                                  ),
                                  '원'
                                )}
                              </p>
                            </Flex>
                          );
                        })}
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              ),
          },
          {
            key: 'price',
            title: '갸격',
            dataIndex: 'price',
            render: (item) => stringToMoneyFormat(item),
          },
          {
            key: 'num',
            title: '수량',
            dataIndex: 'num',
          },
          {
            key: 'grade_discount_price',
            title: '등급할인',
            dataIndex: 'grade_discount_price',
            render: (item) => stringToMoneyFormat(item),
          },
          {
            key: 'promotion_discount_price',
            title: '프로모션 할인',
            dataIndex: 'promotion_discount_price',
            render: (item) => stringToMoneyFormat(item),
          },
          {
            key: 'coupon_discount_price',
            title: '쿠폰 할인',
            dataIndex: 'coupon_discount_price',
            render: (item) => stringToMoneyFormat(item),
          },
          {
            key: 'payment_price',
            title: '결제금액',
            dataIndex: 'payment_price',
            render: (item) => stringToMoneyFormat(item),
          },
        ];
      }
      default:
        return [];
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    if (!isOpened) {
      setCashBillInfo(initialValue);
    }
  }, [isOpened]);

  /* ===== RENDER ===== */
  const col = [
    {
      title: '결제번호',
      dataIndex: 'jm_pay_id',
      width: 130,
      align: 'center',
    },
    {
      title: currentTab === 'deduct' ? '차감일 (시간)' : '결제일(시간)',
      dataIndex: 'created_at',
      width: 210,
      align: 'center',
      render: (value) => moment(value * 1000).format('YYYY-MM-DD (HH:mm:ss)'),
      sorter: (a, b) => a.created_at - b.created_at,
    },
    {
      title: '결제수단',
      dataIndex: 'order_route',
      width: 130,
      align: 'center',
      filters: Object.entries(ORDER_ROUTE).map(([key, v]) => ({
        text: v.label,
        value: key,
      })),
      onFilter: (value, record) => record.order_route.includes(value),
    },
    {
      title: '처리금액',
      dataIndex: 'price',
      width: 150,
      align: 'center',
      render: (value) => stringToMoneyFormat(value),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: '분류',
      dataIndex: 'type',
      width: 130,
      align: 'center',
      render: (value) => (
        <Tag color={JM_PAY_TYPE[value]?.color}>{JM_PAY_TYPE[value]?.label}</Tag>
      ),
      filters:
        currentTab === 'history' &&
        Object.entries(JM_PAY_TYPE).map(([key, v]) => ({
          text: v.label,
          value: key,
        })),
      onFilter: (value, record) => record.type.includes(value),
    },
    ...(currentTab === 'charge'
      ? [
          {
            title: '충전 후 잔액',
            dataIndex: 'current_balance',
            width: 150,
            align: 'center',
            render: (value) => stringToMoneyFormat(value ?? 0),
            sorter: (a, b) => a.current_balance - b.current_balance,
          },
        ]
      : []),
    {
      title: '회원명',
      dataIndex: 'user',
      width: 170,
      align: 'center',
      render: (value) => (
        <Link
          style={{ textDecoration: 'underline' }}
          to={`/users/list?modal=${encodeURIComponent(
            encrypt(value?.user_id)
          )}`}
        >
          {`${value?.user_name}(${value?.user_gender}, ${
            value?.user_nickname ?? '-'
          })`}
        </Link>
      ),
    },
    {
      title: '연락처',
      dataIndex: 'user',
      // width: 150,
      align: 'center',
      render: (user) => user?.user_phone,
    },
    // ...(currentTab === 'history'
    //   ? [
    //       {
    //         title: '현금영수증 발급',
    //         dataIndex: 'type',
    //         // width: 150,
    //         align: 'center',
    //         render: (type, record) =>
    //           type === 'USE' ? (
    //             record?.issued_cash_bill ? (
    //               record?.cancel_cash_bill ? (
    //                 <Flex vertical>
    //                   <Flex gap={4}>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       상점번호
    //                     </Text>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.cancel_cash_bill?.tid}
    //                     </Text>
    //                   </Flex>
    //                   <Flex gap={4}>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       취소번호
    //                     </Text>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.cancel_cash_bill?.cashbillNumb}
    //                     </Text>
    //                   </Flex>
    //                   <Flex gap={4}>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       취소금액
    //                     </Text>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.cancel_cash_bill?.cancelAmount}
    //                     </Text>
    //                   </Flex>
    //                   <Flex gap={4}>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       취소일시
    //                     </Text>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.cancel_cash_bill?.tradeDateTime}
    //                     </Text>
    //                   </Flex>
    //                   <Flex>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.cancel_cash_bill?.respMessage}
    //                     </Text>
    //                   </Flex>
    //                 </Flex>
    //               ) : (
    //                 <Flex
    //                   vertical
    //                   style={{ position: 'relative', top: 0, left: 0 }}
    //                 >
    //                   <Flex gap={4}>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       상점번호
    //                     </Text>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.issued_cash_bill?.tid}
    //                     </Text>
    //                   </Flex>
    //                   <Flex gap={4}>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       승인번호
    //                     </Text>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.issued_cash_bill?.cashbillNumb}
    //                     </Text>
    //                   </Flex>
    //                   <Flex gap={4}>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       승인금액
    //                     </Text>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.issued_cash_bill?.totalAmount}
    //                     </Text>
    //                   </Flex>
    //                   <Flex gap={4}>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       승인일시
    //                     </Text>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.issued_cash_bill?.tradeDateTime}
    //                     </Text>
    //                   </Flex>
    //                   <Flex>
    //                     <Text size={12} color={'#BDBDBD'}>
    //                       {record?.issued_cash_bill?.respMessage}
    //                     </Text>
    //                   </Flex>

    //                   {record?.order_status === 'REFUNDED' ? (
    //                     <Button
    //                       size="small"
    //                       danger
    //                       style={{
    //                         position: 'absolute',
    //                         top: '50%',
    //                         right: 0,
    //                         transform: 'translateY(-50%)',
    //                       }}
    //                       onClick={(e) => {
    //                         onIssue('CANCEL', record);
    //                         e.stopPropagation();
    //                       }}
    //                     >
    //                       취소
    //                     </Button>
    //                   ) : null}
    //                 </Flex>
    //               )
    //             ) : (
    //               record?.order_status === 'COMPLETED' && (
    //                 <Button
    //                   size="small"
    //                   type="primary"
    //                   onClick={(e) => {
    //                     setCashBillInfo((prev) => ({
    //                       ...prev,
    //                       productName: `${
    //                         record?.order_data?.product_information?.[0]
    //                           ?.menu_name
    //                       }${
    //                         record?.order_data?.product_information?.length > 1
    //                           ? ` 외 ${record?.order_data?.product_information?.[0]?.menu_name}${record?.order_data?.product_information?.length}건`
    //                           : ''
    //                       }`,
    //                       totalAmount: Math.abs(record?.price),
    //                       PHONE_NUMB: record?.user?.user_phone,
    //                       userName: record?.user?.user_name,
    //                       data: record,
    //                     }));
    //                     setIsOpened(!isOpened);
    //                     e.stopPropagation();
    //                   }}
    //                 >
    //                   발급
    //                 </Button>
    //               )
    //             )
    //           ) : (
    //             '-'
    //           ),
    //       },
    //     ]
    //   : []),

    ...(currentTab === 'use'
      ? [
          {
            title: '사용처',
            dataIndex: 'order',
            width: 150,
            align: 'center',
            render: (_, record) =>
              record.order_data ? (
                <Tag>
                  {record.order_data?.shop_information?.type === 'STORE'
                    ? record.order_data?.shop_information?.shop_information_name
                    : '쇼핑몰'}
                </Tag>
              ) : (
                '-'
              ),
          },
          {
            title: '주문내역',
            dataIndex: 'order',
            width: 300,
            align: 'center',
            render: (_, record) =>
              record.order_data ? (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    setIsVisible(true);
                    setOrderData({
                      type: record.order_type,
                      data: record.order_data,
                    });
                  }}
                >
                  보기
                </Button>
              ) : (
                '주문내역을 찾을 수 없음.'
              ),
          },
        ]
      : []),
  ];
  // JSX: start
  return (
    <>
      <Content maxWidth={'100%'} padding={0} backgroundColor={'none'}>
        {/* === 상단 > 타이틀 === */}
        <Title
          size={5}
          style={{ marginBottom: 7.5 }}
          subButton={
            <Button
              onClick={() =>
                downloadXlsx({
                  columns: col.map((column) => {
                    if (column?.title === '연락처') {
                      column.dataIndex = 'user_phone';
                    }
                    return column;
                  }),
                  rows: data?.map((_item) => {
                    const user = _item?.user;
                    return {
                      jm_pay_id: _item?.jm_pay_id,
                      created_at: moment(_item?.created_at * 1000).format(
                        'YYYY-MM-DD (HH:mm:ss)'
                      ),
                      order_route: _item?.order_route,
                      price: stringToMoneyFormat(_item?.price),
                      type: JM_PAY_TYPE[_item?.type]?.label,
                      current_balance: stringToMoneyFormat(
                        _item?.current_balance ?? 0
                      ),
                      user: `${user?.user_name}(${user?.user_gender}, ${
                        user?.user_nickname ?? '-'
                      })`,
                      user_phone: user?.user_phone,
                    };
                  }),
                  fileName: `제이엠페이_${
                    currentTab === 'history'
                      ? '전체'
                      : currentTab === 'charge'
                      ? '충전'
                      : currentTab === 'cancel'
                      ? '취소'
                      : '환불'
                  }내역`,
                })
              }
            >
              엑셀로 다운로드
            </Button>
          }
        >
          이용내역({data?.length ?? 0})
        </Title>

        {/* ===== 데이터 테이블: start ===== */}
        <div style={{ overflow: 'auto', width: '100%', height: '100%' }}>
          <Table
            className="boxShadow"
            bordered
            size="small"
            dataSource={data ?? []}
            scroll={{
              x: 'max-content',
            }}
            style={{ minHeight: 580 }}
            onRow={(record) => {
              return {
                onClick:
                  currentTab === 'use' ? () => {} : () => onSelect(record), // 행 클릭 시 이벤트 처리
              };
            }}
            columns={col}
          />
        </div>
        {/* ===== 데이터 테이블: end ===== */}
      </Content>

      {/* ===== 주문내역모달: start ===== */}
      <ModalLayout
        type="drawer"
        open={isVisible && orderData}
        title="주문상세"
        closable={true}
        onCancel={() => {
          setOrderData(null);
          setIsVisible(!isVisible);
        }}
      >
        <Table
          dataSource={getData(orderData?.data, orderData?.type)}
          columns={getColumns(orderData?.type)}
        />
      </ModalLayout>
      {/* ===== 주문내역모달: end ===== */}

      {/* ===== 상세모달: start ===== */}
      <ModalLayout
        // type="drawer"
        // centered
        // maxWidth={710}
        width={680}
        minHeight={600}
        isOpen={currentRow !== null}
        open={currentRow !== null}
        title="주문상세"
        closable={true}
        onCancel={() => onSelect(null)}
        footer={
          <>
            <Button
              onClick={() => {
                setExpansionUser(false);
                onSelect(null);
              }}
            >
              닫기
            </Button>
          </>
        }
      >
        <Divider style={{ margin: '13px 0' }} />
        {currentRow && (
          <Content maxWidth={'100%'} padding={0}>
            {/* === 주문자 정보: start === */}
            <Title size={5} style={{ marginBottom: 5 }}>
              JM페이 주문정보
            </Title>
            <Descriptions
              bordered
              column={2}
              size="small"
              labelStyle={styles.labelStyle}
              contentStyle={styles.contentStyle}
              style={styles.defaultStyle}
              items={[
                // === Line 1 === //
                {
                  key: `key11`,
                  label: '결제일(시간)',
                  children: moment(currentRow.created_at * 1000).format(
                    'YYYY-MM-DD(HH:mm:ss)'
                  ),
                },
                {
                  key: `key12`,
                  label: '주문번호',
                  children: currentRow.jm_pay_id,
                },
                // === Line 2 === //
                {
                  key: `key21`,
                  label: '처리금액',
                  children: stringToMoneyFormat(currentRow.price),
                },
                {
                  key: `key22`,
                  label: '결제주체',
                  children: currentRow.order_route,
                },
                // === Line 3 === //
                {
                  key: `key31`,
                  label: '유형',
                  children: (
                    <Tag color={JM_PAY_TYPE[currentRow.type]?.color}>
                      {JM_PAY_TYPE[currentRow.type]?.label}
                    </Tag>
                  ),
                },
                {
                  key: `key32`,
                  label: 'JM페이 잔액',
                  children: stringToMoneyFormat(currentRow?.jm_pay, 'P'),
                },
                // === Line 4 === //
                ...(currentRow?.payment_info
                  ? [
                      {
                        key: `key41`,
                        label: '충전주체',
                        children: currentRow?.payment_info?.msg1 ?? '-',
                      },
                      {
                        key: `key42`,
                        label: 'PG결제결과',
                        children: currentRow?.payment_info?.msg2 ?? '-',
                      },
                    ]
                  : []),
              ]}
            />
            {/* === 주문자 정보: end === */}
            <br />
            <br />

            <Title size={5} style={{ marginBottom: 5 }}>
              회원 정보
            </Title>
            <Descriptions
              bordered
              column={2}
              size="small"
              labelStyle={styles.labelStyle}
              contentStyle={styles.contentStyle}
              style={styles.defaultStyle}
              items={[
                {
                  key: '1',
                  label: '회원번호',
                  children: `${currentRow?.user?.user_id}`,
                },
                {
                  key: '2',
                  label: '회원명',
                  children: `${currentRow?.user?.user_name ?? '-'}`,
                },
                {
                  key: '3',
                  label: '회원 아이디',
                  children: `${currentRow?.user?.user_account ?? '-'}`,
                },
                {
                  key: '4',
                  label: '계정상태',
                  children: currentRow?.user?.account_status,
                },
                {
                  key: '5',
                  label: '연락처',
                  children: `${currentRow?.user?.user_phone ?? '-'}`,
                },
                {
                  key: '6',
                  label: '이메일',
                  children: `${currentRow?.user?.user_email ?? '-'}`,
                },

                // 확장 버튼 클릭 시 열리는 정보
                ...(expansionUser === true
                  ? [
                      {
                        key: '7',
                        label: '성별',
                        children: `${
                          currentRow?.user?.user_gender === 'M' ? '남' : '여'
                        }`,
                      },
                      {
                        key: '8',
                        label: '생년월일',
                        children: `${Formatter.unixToDate(
                          currentRow?.user?.user_birthday,
                          'YYYY.MM.DD'
                        )}`,
                      },
                      {
                        key: '18',
                        label: '회원등급',
                        children: `${
                          currentRow?.user?.grade?.grade_name ?? '-'
                        }`,
                      },
                      {
                        key: '17',
                        label: '메모',
                        children: currentRow?.user?.memo,
                      },
                      {
                        key: '9',
                        label: '주소',
                        children: `${currentRow?.user?.user_address ?? '-'}\n${
                          currentRow?.user?.user_address_detail ?? ''
                        }`,
                        span: 2,
                      },
                      {
                        key: '10',
                        label: '닉네임',
                        children: `${currentRow?.user?.user_nick_name ?? '-'}`,
                      },
                      {
                        key: '11',
                        label: '멤버십 번호',
                        children: `${currentRow?.user?.membership_code ?? '-'}`,
                      },
                      {
                        key: '12',
                        label: '가입일',
                        children: `${Formatter.unixToDate(
                          currentRow?.user?.created_at,
                          'YYYY.MM.DD HH:mm:ss'
                        )}`,
                      },
                      {
                        key: '13',
                        label: '최근 접속일',
                        children: `${Formatter.unixToDate(
                          currentRow?.user?.access_time,
                          'YYYY.MM.DD HH:mm:ss'
                        )}`,
                      },
                      {
                        key: '14',
                        label: '가입경로',
                        children: `${
                          currentRow?.user?.social_type
                            ? SIGNUP_PATH[currentRow?.user?.social_type]
                            : currentRow?.user?.user_name
                            ? SIGNUP_PATH['MEMBER']
                            : SIGNUP_PATH['NONE']
                        }`,
                      },
                      {
                        key: '15',
                        label: '보유쿠폰',
                        children: (
                          <p>
                            <button
                              style={{
                                border: 'none',
                                background: 'none',
                                color: '#1C6BFF',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                // setIsOpened(!isOpened);
                                // setModalType('COUPON');
                              }}
                            >
                              {stringToMoneyFormat(
                                currentRow?.user?.couponStorage?.useable
                                  ?.length,
                                '개'
                              )}
                            </button>
                          </p>
                        ),
                      },
                      {
                        key: '16',
                        label: '회원설정',
                        children: (
                          <div>
                            {Object.entries(
                              currentRow?.user?.user_setting || {}
                            ) // user_setting이 없으면 빈 객체 처리
                              .map(([key, value], index) => {
                                if (!USER_SETTING[key]) return null; // 조건이 충족되지 않으면 null 반환

                                return (
                                  <p
                                    style={{
                                      // margin: '8px 0px',
                                      margin: 0,
                                      padding: '5px 0',
                                      textAlign: 'left',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      borderTop:
                                        index > 0
                                          ? '1px solid #f1f1f1'
                                          : 'none',
                                    }}
                                  >
                                    <p style={{ margin: 0 }}>
                                      {USER_SETTING[key]}:
                                    </p>{' '}
                                    <p style={{ margin: 0 }}>
                                      {value ? (
                                        <Tag
                                          icon={<CheckCircleOutlined />}
                                          color="success"
                                        >
                                          동의
                                        </Tag>
                                      ) : (
                                        <Tag
                                          icon={<MinusCircleOutlined />}
                                          color="default"
                                        >
                                          미동의
                                        </Tag>
                                      )}
                                    </p>
                                    {/* <p>{value ? '동의' : '미동의'}</p> */}
                                  </p>
                                );
                              })}
                          </div>
                        ),
                        span: 2,
                      },
                    ]
                  : []),
              ]}
            />
            <div>
              {expansionUser !== true ? (
                <Button
                  type="link"
                  block
                  onClick={() => setExpansionUser(true)}
                >
                  <PlusCircleOutlined /> 내용 상세보기
                </Button>
              ) : (
                <Button
                  type="link"
                  block
                  onClick={() => setExpansionUser(false)}
                >
                  <MinusCircleOutlined /> 내용 접기
                </Button>
              )}
            </div>
          </Content>
        )}
        <Divider style={{ margin: '13px 0' }} />
      </ModalLayout>
      {/* ===== 상세모달: end ===== */}

      {/* ===== 현금영수증: start ===== */}
      <ModalLayout
        open={isOpened}
        onCancel={() => setIsOpened(false)}
        width={500}
        centered
        footer={
          <Button
            onClick={async () => {
              const res = await onIssue('issue', cashBillInfo?.data);
              if (res) {
                setIsOpened(false);
              }
            }}
          >
            발급
          </Button>
        }
      >
        <Content maxWidth={'100%'}>
          <Flex vertical gap={10}>
            <Radio.Group
              value={cashBillInfo?.userInfoType}
              onChange={(e) =>
                setCashBillInfo((prev) => ({
                  ...prev,
                  userInfoType: e.target.value,
                }))
              }
              options={[
                { label: '휴대폰번호', value: 'PHONE_NUMB' },
                { label: '사업자번호', value: 'BIZ_NUMB' },
              ]}
              optionType="button"
              buttonStyle="solid"
            />

            <Input
              value={cashBillInfo[cashBillInfo?.userInfoType]}
              onChange={(e) =>
                setCashBillInfo((prev) => ({
                  ...prev,
                  [cashBillInfo?.userInfoType]: e.target.value,
                }))
              }
              placeholder={
                cashBillInfo?.userInfoType === 'PHONE_NUMB'
                  ? '휴대폰 번호'
                  : '사업자 번호'
              }
            />

            <InputNumber
              value={cashBillInfo?.totalAmount}
              placeholder="승인금액"
              onChange={(e) =>
                setCashBillInfo((prev) => ({
                  ...prev,
                  totalAmount: e,
                }))
              }
              style={{ width: '100%' }}
              min={1}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
            />
          </Flex>
        </Content>
      </ModalLayout>
      {/* ===== 현금영수증: end ===== */}
    </>
  ); // JSX: end
};

/*  */
const styles = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 20,
  },
  button: {
    width: 80,
    height: 40,
    fontSize: 15,
    fontWeight: 600,
    margin: '0px 4px',
  },
  form: {
    input: {
      large: {
        width: 175,
        marginRight: 8,
      },
      medium: {
        width: 100,
        marginRight: 8,
      },
    },
    button: {
      marginRight: 8,
    },
    date: {
      width: 150,
    },
    text: {
      fontSize: 13,
      fontWeight: 400,
    },
  },

  couponLayout: {
    width: '100%',
    backgroundColor: '#f4f4f4',
    borderLeft: '20px solid #ddd',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: 20,
    paddingLeft: 20,
    position: 'relative',
    top: 0,
    left: 0,
  },
  couponTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 16,
  },
  couponStatus: {
    color: 'var(--black-color)',
    fontSize: 16,
    lineHeight: 1.75,
  },
  couponDate: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--dark-gray-color)',
  },

  labelStyle: {
    // width: 155,
    width: '21%',
    color: '#111',
    fontWeight: '500',
    background: '#f4f4f4',
  },
  contentStyle: { width: '29%' },
  defaultStyle: {
    borderTop: '2px solid #d1d1d1',
    borderRadius: 5,
    marginTop: 5,
  },
};

/* DF */
ChargingHistory.defaultProps = {
  onSelect: () => {},
};
export default ChargingHistory;
